import * as React from "react";
import axios from "axios";
import { Loader2, AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./dialog";
import { Button } from "./button";
import { Text, MonoText } from "./typography";
import { CurrencyValue } from "./value-display";
import { API_BASE_URL } from '../../lib/api';

/**
 * DeleteContributionDialog - Confirmation dialog for deleting a contribution
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onOpenChange - Callback when open state changes
 * @param {Function} props.onSuccess - Callback when contribution is deleted successfully
 * @param {Object} props.contribution - The contribution to delete
 */
export function DeleteContributionDialog({
  open,
  onOpenChange,
  onSuccess,
  contribution,
}) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Reset error when dialog opens
  React.useEffect(() => {
    if (open) {
      setError(null);
    }
  }, [open]);

  const handleDelete = async () => {
    if (!contribution?.contributionId) {
      setError("No contribution selected");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await axios.delete(
        `${API_BASE_URL}/contributions/${contribution.contributionId}`,
        { withCredentials: true }
      );

      if (onSuccess) {
        onSuccess();
      }
      onOpenChange(false);
    } catch (err) {
      let errorMsg =
        err.response?.data?.details ||
        err.response?.data?.error ||
        err.message;

      if (typeof errorMsg === "object" && errorMsg !== null) {
        errorMsg = errorMsg.message || JSON.stringify(errorMsg);
      }

      console.error("Failed to delete contribution:", err);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!contribution) {
    return null;
  }

  const isWithdrawal = contribution.type === "withdrawal";
  const typeLabel = isWithdrawal ? "Withdrawal" : "Deposit";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="pb-2">
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Delete {typeLabel}
          </DialogTitle>
          <DialogDescription>
            This action cannot be undone. This will permanently delete the
            {isWithdrawal ? " withdrawal" : " deposit"} record.
          </DialogDescription>
        </DialogHeader>

        {/* Error Message */}
        {error && (
          <div className="rounded-md bg-destructive/10 border border-destructive p-3">
            <Text variant="error" size="sm">
              {error}
            </Text>
          </div>
        )}

        {/* Contribution Details */}
        <div className="rounded-lg border border-border/50 p-4 space-y-2">
          <div className="flex justify-between items-center">
            <Text variant="secondary" size="sm">Type</Text>
            <Text size="sm" className="font-medium capitalize">
              {contribution.type}
            </Text>
          </div>
          <div className="flex justify-between items-center">
            <Text variant="secondary" size="sm">Amount</Text>
            <CurrencyValue
              value={contribution.amount}
              currency={contribution.currency}
              size="sm"
              className="font-medium"
            />
          </div>
          <div className="flex justify-between items-center">
            <Text variant="secondary" size="sm">Date</Text>
            <MonoText size="sm">{contribution.date}</MonoText>
          </div>
          {contribution.description && (
            <div className="flex justify-between items-start">
              <Text variant="secondary" size="sm">Description</Text>
              <Text size="sm" className="text-right max-w-[200px]">
                {contribution.description}
              </Text>
            </div>
          )}
        </div>

        <DialogFooter className="pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DeleteContributionDialog;
